import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { constant } from "common/constant";
import { getApi } from "common/services/api";
import { putApi } from "common/services/api";
import { postApi } from "common/services/api";
import { deleteApi } from "common/services/api";
import { API_PATHS } from "common/services/apiPaths";
import { toast } from "react-toastify";

// Debug: Log API paths
console.log('API Paths loaded:', {
  RECRUITMENT_GUIDE_UPLOAD: API_PATHS.RECRUITMENT_GUIDE_UPLOAD,
  baseUrl: constant.baseUrl,
});

export const getRecruitmentGuide = createAsyncThunk(
  `recruitmentGuide/getRecruitmentGuide`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await getApi(API_PATHS.RECRUITMENT_GUIDE);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || constant.apiReqFailed);
    }
  }
);

export const uploadRecruitmentGuide = createAsyncThunk(
  `recruitmentGuide/uploadRecruitmentGuide`,
  async ({ values, resetForm }, { dispatch, rejectWithValue }) => {
    try {
      // Validate payload before sending
      if (!values || !values.pdfUrl || values.pdfUrl.trim() === '') {
        const errorMsg = "PDF file is required";
        toast.error(errorMsg);
        return rejectWithValue(errorMsg);
      }

      const fileExtension = values.pdfUrl.split('.').pop()?.toLowerCase();
      if (fileExtension !== 'pdf') {
        const errorMsg = "Only PDF files are allowed";
        toast.error(errorMsg);
        return rejectWithValue(errorMsg);
      }

      // Log payload for debugging
      console.log('Uploading recruitment guide with payload:', {
        title: values.title,
        description: values.description,
        pdfUrl: values.pdfUrl?.substring(0, 50) + '...', // Log first 50 chars
      });

      console.log('=== Making API Call ===');
      console.log('Full API URL:', constant.baseUrl + API_PATHS.RECRUITMENT_GUIDE_UPLOAD);
      console.log('Payload being sent:', JSON.stringify(values, null, 2));

      const response = await postApi(API_PATHS.RECRUITMENT_GUIDE_UPLOAD, values);

      console.log('=== API Call Successful ===');
      console.log('Response status:', response?.status);
      console.log('Response data:', response?.data);
      toast.success("Recruitment guide uploaded successfully");
      await dispatch(getRecruitmentGuide());

      return response.data;
    } catch (error) {
      console.error('Upload recruitment guide error:', error);
      console.error('Error response:', error?.response);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to upload recruitment guide";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateRecruitmentGuide = createAsyncThunk(
  "recruitmentGuide/updateRecruitmentGuide",
  async ({ values, resetForm }, { dispatch, rejectWithValue }) => {
    try {
      const response = await putApi(API_PATHS.RECRUITMENT_GUIDE, values);

      toast.success("Recruitment guide updated successfully");
      await dispatch(getRecruitmentGuide());

      return response?.data;
    } catch (err) {
      toast.error("Failed to update recruitment guide");
      return rejectWithValue(err.message);
    }
  }
);

export const downloadRecruitmentGuide = createAsyncThunk(
  `recruitmentGuide/downloadRecruitmentGuide`,
  async (_, { rejectWithValue }) => {
    try {
      // Backend returns JSON with presigned URL
      const response = await getApi(API_PATHS.RECRUITMENT_GUIDE_DOWNLOAD);
      const downloadUrl = response?.data?.downloadUrl || response?.downloadUrl;
      
      if (!downloadUrl) {
        throw new Error("Download URL not found in response");
      }
      
      return { downloadUrl };
    } catch (error) {
      console.error("Download recruitment guide error:", error);
      toast.error("Failed to download recruitment guide");
      return rejectWithValue(error.message || constant.apiReqFailed);
    }
  }
);

export const deleteRecruitmentGuide = createAsyncThunk(
  `recruitmentGuide/deleteRecruitmentGuide`,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // deleteApi expects path + param, but for DELETE without params, pass empty string
      const response = await deleteApi(API_PATHS.RECRUITMENT_GUIDE_DELETE, "");
      toast.success("Recruitment guide deleted successfully");
      await dispatch(getRecruitmentGuide()); // Refresh data
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to delete recruitment guide";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const recruitmentGuideSlice = createSlice({
  name: "recruitmentGuide",
  initialState: {
    data: null,
    getLoading: false,
    uploadLoading: false,
    updateLoading: false,
    downloadLoading: false,
    deleteLoading: false,
    error: null,
  },
  reducers: {
    clearRecruitmentGuide: (state) => {
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Recruitment Guide
      .addCase(getRecruitmentGuide.pending, (state) => {
        state.getLoading = true;
        state.error = null;
      })
      .addCase(getRecruitmentGuide.fulfilled, (state, action) => {
        state.getLoading = false;
        state.data = action.payload;
      })
      .addCase(getRecruitmentGuide.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
      })
      // Upload Recruitment Guide
      .addCase(uploadRecruitmentGuide.pending, (state) => {
        state.uploadLoading = true;
        state.error = null;
      })
      .addCase(uploadRecruitmentGuide.fulfilled, (state) => {
        state.uploadLoading = false;
      })
      .addCase(uploadRecruitmentGuide.rejected, (state, action) => {
        state.uploadLoading = false;
        state.error = action.payload;
      })
      // Update Recruitment Guide
      .addCase(updateRecruitmentGuide.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateRecruitmentGuide.fulfilled, (state, action) => {
        state.updateLoading = false;
        // Don't update state.data here - getRecruitmentGuide() will update it with fresh data
        // This ensures we have the latest PDF URL and pdfGeneratedAt timestamp
      })
      .addCase(updateRecruitmentGuide.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })
      // Download Recruitment Guide
      .addCase(downloadRecruitmentGuide.pending, (state) => {
        state.downloadLoading = true;
        state.error = null;
      })
      .addCase(downloadRecruitmentGuide.fulfilled, (state) => {
        state.downloadLoading = false;
      })
      .addCase(downloadRecruitmentGuide.rejected, (state, action) => {
        state.downloadLoading = false;
        state.error = action.payload;
      })
      // Delete Recruitment Guide
      .addCase(deleteRecruitmentGuide.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteRecruitmentGuide.fulfilled, (state) => {
        state.deleteLoading = false;
        state.data = null; // Clear data after deletion
      })
      .addCase(deleteRecruitmentGuide.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearRecruitmentGuide } = recruitmentGuideSlice.actions;
export default recruitmentGuideSlice.reducer;

